<script setup lang="ts">
  import {
    ref,
    reactive,
    watch,
    onMounted,
    onBeforeUnmount,
    nextTick,
    computed,
    h,
    provide
  } from 'vue'
  import { pendingRefocusKey } from '@/shared/models/pendingRefocus'
  import Splitter from 'primevue/splitter'
  import SplitterPanel from 'primevue/splitterpanel'
  import { Card, Button, Drawer, Modal, message, Select, Spin, Input } from 'ant-design-vue'
  import {
    PlusOutlined,
    CodeOutlined,
    HolderOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined
  } from '@ant-design/icons-vue'
  import { Calculation } from './models/Calculation'
  import { CalculationSchema } from './models/CalculationSchema'
  import CalculationSelectionModal from './components/CalculationSelectionModal.vue'
  import { Calculator, ChevronRight, Download, FileCog, Loader2, Trash2 } from 'lucide-vue-next'
  import CalculationConfiguration from './components/CalculationConfiguration.vue'
  import { CalculationsService } from './services/CalculationsService'
  import { defaultMessageDurationSeconds } from '@/utils'
  import CalculationJsonManagement from './components/CalculationJsonManagement.vue'
  import { debounceTime, Subject } from 'rxjs'
  import { CalculationsJsonParsingService } from './services/CalculationsJsonParsingService'
  import JsonCalculationConfiguration from './components/JsonCalculationConfiguration.vue'
  import { CalculationsTypeGuardService } from './services/CalculationsTypeGuardService'
  import { CalculationsSearchService } from './services/CalculationsSearchService'
  import { CalculationsUiService } from './services/CalculationsUiService'
  import { CalculationsJsonCalculationConfigurationService } from './services/CalculationJsonCalculationConfigurationService'
  import { InjectionService } from '@/shared/services/InjectionService'
  import { CalculationsApiService, ICalculationsApiService } from './services/CalculationApiService'
  import { SchemaStoreService } from '@/shared/services/SchemaStoreService'
  import { CalculationSchemaUiFieldGroup } from './models/CalculationSchemaUi'
  import { useWindowSize } from '@/composables/useWindowSize'
  import { AplosFile } from '../FileManager/models/AplosFile'
  import { useAppLayoutMode } from '@/composables/useAppLayoutMode'
  import { AppLayoutMode } from '@/models/AppLayoutMode'
  import { Execution } from '@/shared/Executions/models/Execution'
  import { AnalysisWizardModel } from '../AnalysisWizard/models/AnalysisWizardModel'
  import { useUser } from '@/composables/useUser'
  import {
    GetExecutionOutputFileArgs,
    GetExecutionOutputFileType
  } from '@/shared/Executions/models/GetExecutionOutputFileArgs'
  import { toast } from 'vue-sonner'
  import ExecutionSelectionTable from '../ExecutionHistory/components/ExecutionSelectionTable.vue'
  import { ExecutionPhaseType } from '@/shared/Executions/models/ExecutionPhaseType'
  import { ExecutionStatusType } from '@/shared/Executions/models/ExecutionStatusType'
  import { WizardStepStatusType } from '@/shared/Wizard/models/WizardStep'
  import { AnalysisWizardNavigationService } from '../AnalysisWizard/services/AnalysisWizardNavigationService'
  import { QueueExecutionResponse } from '@/shared/Executions/models/QueueExecutionResponse'
  import { AnalysisWizardService } from '../AnalysisWizard/services/AnalysisWizardService'
  import DataViewer from '../DataViewer.vue'
  import { AnalysisWizardStepType } from '../AnalysisWizard/models/AnyalsisWizardStepType'
  import { downloadFile, extractFilename } from '@/utils/download'
  import { GetExecutionOutputPackageArgs } from '@/shared/Executions/models/GetExecutionOutputPackageArgs'
  import { GetFileDataResponse } from '../FileManager/models/GetFileDataResponse'
  import {
    ExecutionApiService,
    IExecutionApiService
  } from '@/shared/Executions/services/ExecutionApiService'
  import {
    ExecutionPhaseService,
    IExecutionPhaseService
  } from '@/shared/Executions/services/ExecutionPhaseService'
  import ExecutionStatusComponent from '@/shared/Executions/components/ExecutionStatusComponent.vue'
  import { CdnSchemaFactory } from '@/shared/CdnSchema/services/CdnSchemaFactory'
  import { CdnSchemaType } from '@/shared/CdnSchema/models/CdnSchemaType'

  interface ICalculationsProps {
    customCalculations?: Array<Calculation>
    customCalculationsExecution?: Execution
    analysisExecution?: Execution
    pkResultsFile?: AplosFile
    sortColumns?: Array<string>
    wizardModel?: AnalysisWizardModel
    analysisSelectionDisabled?: boolean // in the wizard this component should rely on an input analysisExecution
  }

  const wizardNavigationService: AnalysisWizardNavigationService = InjectionService.injectStrict(
    AnalysisWizardNavigationService.injectableKey
  )

  const cdnSchemaFactory: CdnSchemaFactory = InjectionService.injectStrict(
    CdnSchemaFactory.injectableKey
  )

  const customcalculationsApiService: ICalculationsApiService = InjectionService.injectStrict(
    CalculationsApiService.injectableKey
  )

  const calculationsJsonParsingService: CalculationsJsonParsingService =
    InjectionService.injectStrict(CalculationsJsonParsingService.injectableKey)

  const executionApiService: IExecutionApiService = InjectionService.injectStrict(
    ExecutionApiService.injectableKey
  )

  const executionPhaseService: IExecutionPhaseService = InjectionService.injectStrict(
    ExecutionPhaseService.injectableKey
  )

  const customSchemaStore = SchemaStoreService.useCustomSchemaStore()

  const props = defineProps<ICalculationsProps>()

  const emit = defineEmits<{
    (e: 'update:calculations', value: Array<Calculation>): void
    (e: 'update:sortColumns', value: Array<string>): void
    (e: 'update:customCalculationsExecution', value?: Execution): void
    (e: 'update:pkResultsFile', value?: AplosFile): void
    (e: 'update:pkResultsCustomCalcsFile', value?: AplosFile): void
  }>()

  const pendingRefocus = ref<{ levelFieldKey: string; cursor: number | null } | null>(null)
  provide(pendingRefocusKey, pendingRefocus)

  const executionStatusComponent = ref<InstanceType<typeof ExecutionStatusComponent> | null>(null)

  const executionName = ref<string>(props.customCalculationsExecution?.name ?? '')
  const execution = ref<Execution | undefined>(props.customCalculationsExecution)

  const isExecuting = computed(
    () =>
      [
        ExecutionStatusType.Succeeded,
        ExecutionStatusType.Cancelled,
        ExecutionStatusType.Failed,
        ExecutionStatusType.Unknown
      ].indexOf(execution.value?.status ?? ExecutionStatusType.Unknown) === -1
  )

  const isExecuted = computed(() => execution.value?.id != null)

  watch(
    () => props.customCalculationsExecution,
    (val) => {
      execution.value = val
    },
    { immediate: true }
  )

  watch(
    () => executionName.value,
    (val) => {
      // invalidate existing execution if previously executed and use new instance to store name
      if (execution.value?.id || !execution.value) {
        execution.value = { name: val } as Execution
      } else if (execution.value) {
        execution.value.name = val
      }
      emit('update:customCalculationsExecution', execution.value)
    },
    { immediate: false }
  )

  const auth = useUser()

  const isDownloadingResults = ref<boolean>(false)
  const isLoadingPkResultsFile = ref<boolean>(false)
  const selectedPkResultsFile = ref<AplosFile | undefined>(props.pkResultsFile)
  const isLoadingAvailableCalculations = ref<boolean>(false)
  const hasErrorLoadingCalculations = ref<boolean>(false)
  const availableCalculationSchemas = ref<CalculationSchema[]>([])
  const activeCalculations = reactive<Calculation[]>([])
  const selectedCalculation = ref<Calculation | null>(null)
  const showCalculationSelection = ref(false)
  const showJsonManagement = ref(false)
  const showAnalysisSelectionModal = ref(false)
  const selectedAnalysisExecutionId = ref<string>()

  const lastGetFileDataResponse = ref<GetFileDataResponse>()

  const sortColumns = ref<Array<string>>(props.sortColumns ?? [])

  const availableColumns = ref<Array<{ value: string; label: string }>>([])
  const availableParams = ref<Array<{ value: string; label: string }>>([])

  const loadAvailableColumnsAndParamCodes = (pkResultsFile?: AplosFile) => {
    availableColumns.value = []
    availableParams.value = []

    let columnNames = pkResultsFile?.metadata?.columnNames

    if (columnNames?.length) {
      // sort alphanumerically (natural numeric-aware ordering) then map
      const sortedCols = [...columnNames].sort((a, b) =>
        String(a)
          .trim()
          .localeCompare(String(b).trim(), undefined, { numeric: true, sensitivity: 'base' })
      )
      availableColumns.value = sortedCols.map((name) => ({ value: name, label: name }))
    }

    let paramCodes = pkResultsFile?.metadata?.paramList
    if (paramCodes?.length) {
      // sort param list by paramcd alphanumerically then map
      const sortedParams = [...paramCodes].sort((a, b) =>
        String(a.paramcd).trim().localeCompare(String(b.paramcd).trim(), undefined, {
          numeric: true,
          sensitivity: 'base'
        })
      )
      availableParams.value = sortedParams.map((param) => ({
        value: param.paramcd,
        label: `${param.paramcd} (${param.param})`
      }))
    }
  }

  const setPkResultsFileForAnalysisExecution = async (
    analysisExecution: Execution | string
  ): Promise<AplosFile> => {
    const analysisExecutionId =
      typeof analysisExecution === 'string' ? analysisExecution : analysisExecution?.id

    if (!analysisExecutionId) {
      toast.error(
        'A valid Analysis Execution is required to load PK Listing File for Calculations.'
      )
    }

    try {
      isLoadingPkResultsFile.value = true
      await auth.initialize()
      const args = new GetExecutionOutputFileArgs({
        userId: auth.user.id as string,
        tenantId: auth.user.tenant_id as string,
        executionId: analysisExecutionId!,
        fileType: GetExecutionOutputFileType.PkResults
      })

      const pkResultsFileReponse = await AnalysisWizardService.runAsyncTaskWithLoading(
        'Loading Inputs',
        props.wizardModel,
        executionApiService.getExecutionOutput.bind(executionApiService),
        args
      )

      if (!pkResultsFileReponse || !pkResultsFileReponse.data) {
        toast.error('No PK Listing file was returned for the given Analysis Execution.')
        throw new Error('PK Listing file response is empty')
      }

      selectedPkResultsFile.value = pkResultsFileReponse.data
      return pkResultsFileReponse.data
    } catch (error) {
      toast.error('An error was encountered loading PK Listing File for Calculations.')
      throw error
    } finally {
      isLoadingPkResultsFile.value = false
    }
  }

  const isProcessingCalculations = ref(false)
  const draggedCalculation = ref<Calculation | null>(null)
  const highlightedCalculationId = ref<string | null>(null)
  const deletingCalculationId = ref<string | null>(null)
  const dragOverIndex = ref<number>(-1)

  const activeCalculationTabKey = ref<string>('view')
  const activeCalculationViewMode = ref<{ mode: 'view' | 'edit' }>({ mode: 'view' })
  const activeCalculationEditConfiguration = ref<{} | null>(null)

  watch(
    selectedCalculation,
    (newVal) => {
      if (newVal) {
        const flattenedCalculations = CalculationsService.getFlattenedCalculations([newVal])
        activeCalculationEditConfiguration.value = flattenedCalculations?.length
          ? flattenedCalculations[0]
          : null
      }
    },
    { deep: false }
  )

  const onCalculationViewModeTabChange = (activeMode: 'view' | 'edit') => {
    if (selectedCalculation.value == null || activeCalculationEditConfiguration.value == null)
      return
    if (activeMode === 'edit') {
      const flattenedCalculations = CalculationsService.getFlattenedCalculations([
        selectedCalculation.value
      ])
      activeCalculationEditConfiguration.value = flattenedCalculations?.length
        ? flattenedCalculations[0]
        : null
    }
  }

  const loadAvailableCalculationSchemas = async () => {
    hasErrorLoadingCalculations.value = false
    isLoadingAvailableCalculations.value = true
    try {
      const calculationSchemas = await cdnSchemaFactory.loadAvailableSchemasForCdnSchemaType(
        CdnSchemaType.CalculationSchema,
        customSchemaStore
      )

      availableCalculationSchemas.value = calculationSchemas
    } catch (error: any) {
      hasErrorLoadingCalculations.value = true
      message.error('Failed to load available calculations.')
    } finally {
      isLoadingAvailableCalculations.value = false
    }
  }

  const addCalculation = (calculationSchema: CalculationSchema): Calculation => {
    const newCalculation =
      CalculationsService.createCalculationFromCalculationSchema(calculationSchema)
    activeCalculations.push(newCalculation)
    selectedCalculation.value = newCalculation
    updateSharedModelOnCalculationsChange()
    message.success(`Added calculation: ${calculationSchema.display_name}`)
    return newCalculation
  }

  const selectCalculation = (calculation: Calculation) => {
    selectedCalculation.value = calculation
  }

  const removeCalculationTimeoutMs: number = 600
  const removeCalculation = (calculation: Calculation) => {
    animateCalculationDeletion(calculation.id as string)
    message.success(`Removed "${calculation.display_name}"`)
  }

  const animateCalculationDeletion = (calculationId: string) => {
    deletingCalculationId.value = calculationId
    setTimeout(() => {
      const idx = activeCalculations.findIndex((c) => c.id === calculationId)
      if (idx > -1) activeCalculations.splice(idx, 1)
      if (selectedCalculation.value?.id === calculationId) {
        if (activeCalculations?.length > 0) {
          selectedCalculation.value = activeCalculations[0]
        } else {
          selectedCalculation.value = null
        }
      }
      deletingCalculationId.value = null
      updateSharedModelOnCalculationsChange()
    }, removeCalculationTimeoutMs)
  }

  const notifyConfigChangedStream$ = new Subject<void>()
  const batchNotifyConfigChangedSubscription = notifyConfigChangedStream$
    .pipe(debounceTime(1800))
    .subscribe(() => {
      message.success('JSON updated', defaultMessageDurationSeconds)
    })

  // Handle file selection from Excution History
  const handleAnalysisExecutionSelected = async (execution: Execution) => {
    showAnalysisSelectionModal.value = false
    const primaryExecutionPhase =
      executionPhaseService.tryGetPrimaryPhaseTypeForExecution(execution)
    if (primaryExecutionPhase !== ExecutionPhaseType.NCAAnalysis) {
      toast.error('Only NCA Analysis executions may be used in configuring custom calculations')
      //return
    }

    selectedAnalysisExecutionId.value = execution?.id
    if (!selectedPkResultsFile.value) {
      const pkResultsFile = await setPkResultsFileForAnalysisExecution(execution)
      emit('update:pkResultsFile', pkResultsFile)
    }

    loadAvailableColumnsAndParamCodes(selectedPkResultsFile.value)
    return
  }

  const clearAnalysisSelection = () => {
    selectedAnalysisExecutionId.value = undefined
    selectedPkResultsFile.value = undefined
    sortColumns.value = []
    availableColumns.value = []
    availableParams.value = []

    if (activeCalculations?.length > 0) {
      Modal.confirm({
        title: 'Confirm',
        content: 'Clear existing calculations?',
        okText: 'Yes',
        cancelText: 'No',
        onOk() {
          activeCalculations.splice(0, activeCalculations.length)
          selectedCalculation.value = null
        },
        onCancel() {}
      })
    }
  }

  const sortColumnsOptions = computed(() => {
    const selected = sortColumns.value
    return availableColumns.value.map((col) => {
      const pos = selected.indexOf(col.value)
      return { value: col.value, label: col.label, pos }
    })
  })

  const getSortColumnsTagLabel = (omitted: { value: string }[]): string => {
    if (omitted.length === 1) {
      const match = availableColumns.value.find((c) => c.value === omitted[0].value)
      return match?.label ?? omitted[0].value
    }
    return `${omitted.length} Columns`
  }

  // Initialize flatRules or load for cleanedFileId and
  const initializeFlatCalculations = (flatCalculations?: Array<Calculation>) => {
    try {
      isLoadingAvailableCalculations.value = true
      const initializedCalculations: Array<Calculation> = []
      let inputCalculations = flatCalculations || []
      inputCalculations.forEach((flatCalculation: Calculation) => {
        const initializedCalculation = calculationsJsonParsingService.initializeEntity(
          flatCalculation,
          availableCalculationSchemas.value,
          1
        )
        initializedCalculations.push(initializedCalculation)
      })
      // Replace the contents of the reactive array so Vue picks up the change

      activeCalculations.splice(0, activeCalculations.length, ...initializedCalculations)
      // Set the first rule as active
      if (activeCalculations.length > 0) selectedCalculation.value = activeCalculations[0]
    } catch {
      message.error('Failed to initialize calculations.')
    } finally {
      isLoadingAvailableCalculations.value = false
    }
  }

  watch(
    () => props.analysisExecution,
    async (analysisExecution?: Execution) => {
      if (analysisExecution) {
        handleAnalysisExecutionSelected(analysisExecution)
      }
    },
    { immediate: true }
  )

  watch(
    sortColumns,
    (newVal: Array<string> | undefined) => {
      emit('update:sortColumns', [...(newVal ?? [])])
    },
    { immediate: true }
  )

  const onSubmitCalculationsForGenerationClicked = async () => {
    if (props.wizardModel && execution.value?.id != null) {
      Modal.confirm({
        title: 'Confirm',
        content: 'Re-executing Custom Calculations will reset any Post Processing Configuration.',
        okText: 'Yes',
        cancelText: 'No',
        onOk: () => {
          submitCalculationsForGeneration()
        },
        onCancel() {}
      })
    } else {
      submitCalculationsForGeneration()
    }
  }

  const submitCalculationsForGeneration = async () => {
    isProcessingCalculations.value = true

    try {
      const auth = useUser()
      await auth.initialize()

      const flattenedCalculations = CalculationsService.getFlattenedCalculations(activeCalculations)

      const args = CalculationsService.createQueueCalculationsArgs(
        selectedAnalysisExecutionId.value as string,
        flattenedCalculations,
        sortColumns.value,
        executionName.value
      )

      emit('update:calculations', args.calculations)

      const response = await AnalysisWizardService.runAsyncTaskWithLoading(
        'Calculating Parameters',
        props.wizardModel,
        customcalculationsApiService.processCalculations.bind(customcalculationsApiService),
        auth?.user?.id as string,
        auth?.user?.tenant_id as string,
        args
      )

      const processCalculationsResponse = response?.data as QueueExecutionResponse
      if (!processCalculationsResponse?.executionId)
        throw Error('An unexpected error has occurred!')
      const successMessage = 'Calculating Parameters'
      /* processRulesResponse?.message?.length
          //? processRulesResponse.message
          : 'Data cleaning queued for processing' */
      message.success(successMessage, defaultMessageDurationSeconds)

      execution.value = {
        id: processCalculationsResponse?.executionId,
        status: ExecutionStatusType.Queued,
        startedUtc: new Date().toISOString()
      } as Execution

      // Emit initial execution step so implementor (wizard) is updated
      emit('update:customCalculationsExecution', execution.value)

      nextTick(() => {
        executionStatusComponent.value?.startPollingStatus()
      })
    } catch (error: any) {
      isProcessingCalculations.value = false
      const errors: Array<string> = error?.response?.data?.errors?.map((x: any) => x.error) || [
        'No specific errors encountered.'
      ]
      Modal.error({
        title: 'Error',
        content: h('div', { style: { marginTop: '5px' } }, [
          h(
            'p',
            { style: { marginBottom: '10px' } },
            'An error(s) was encountered calculating parameters:'
          ),
          h(
            'ul',
            errors.map((err) => h('li', { style: { marginTop: '5px' } }, err))
          )
        ]),
        okText: 'Ok',
        onOk() {}
      })
    }
  }

  //@ts-ignore
  const onExecutionStatusChanged = (status: ExecutionStatusType, ex: Execution) => {
    execution.value = ex
    emit('update:customCalculationsExecution', ex)
  }

  const onExecutionCompleted = async (success: boolean, ex: Execution) => {
    execution.value = ex
    emit('update:customCalculationsExecution', ex)
    emit('update:pkResultsCustomCalcsFile', undefined)
    isProcessingCalculations.value = false
    if (success) {
      updateWizardStepStatus(WizardStepStatusType.Good, 'Good')
    } else {
      message.error('An error was encountered Calculating Parameters')
      updateWizardStepStatus(WizardStepStatusType.Errors, 'Error Calculating Parameters')
    }
  }

  const downloadResultsButtonDisabled = computed(
    () =>
      isProcessingCalculations.value ||
      execution.value?.id == null ||
      execution.value?.status !== ExecutionStatusType.Succeeded
  )

  const downloadResults = async () => {
    try {
      isDownloadingResults.value = true
      await auth.initialize()
      const args = new GetExecutionOutputPackageArgs({
        userId: auth.user.id as string,
        tenantId: auth.user.tenant_id as string,
        executionId: execution.value?.id as string
      })
      const response = await executionApiService.getExecutionOutputPackage(args)
      if (!response.downloadUrl)
        throw Error('Invalid downloadUrl encountered onDownloadActionClicked')
      await downloadFile(response.downloadUrl, extractFilename(response.downloadUrl))
    } catch (error) {
      console.log(error)
      toast.error(`An error occurred trying to download the output package.`)
    } finally {
      isDownloadingResults.value = false
    }
  }

  const moveCalculationUp = (calculation: Calculation) => {
    const currentIndex = activeCalculations.findIndex((c) => c.id === calculation.id)
    if (currentIndex > 0) {
      const temp = activeCalculations[currentIndex]
      activeCalculations[currentIndex] = activeCalculations[currentIndex - 1]
      activeCalculations[currentIndex - 1] = temp
      highlightMovedCalculation(calculation.id as string)
      message.success(`Moved "${calculation.display_name}" up to position ${currentIndex}`)
    }
  }

  const moveCalculationDown = (calculation: Calculation) => {
    const currentIndex = activeCalculations.findIndex((c) => c.id === calculation.id)
    if (currentIndex < activeCalculations.length - 1) {
      const temp = activeCalculations[currentIndex]
      activeCalculations[currentIndex] = activeCalculations[currentIndex + 1]
      activeCalculations[currentIndex + 1] = temp
      highlightMovedCalculation(calculation.id as string)
      message.success(`Moved "${calculation.display_name}" down to position ${currentIndex + 2}`)
    }
  }

  const highlightMovedCalculation = (calculationId: string) => {
    highlightedCalculationId.value = calculationId
    setTimeout(() => {
      highlightedCalculationId.value = null
    }, 1500)
  }

  // Drag and drop handlers
  const handleDragStart = (event: DragEvent, calculation: Calculation) => {
    draggedCalculation.value = calculation
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', calculation.id as string)
    }
  }

  const handleDragOver = (event: DragEvent, index: number) => {
    event.preventDefault()
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move'
    }
    dragOverIndex.value = index
  }

  const handleDragLeave = () => {
    dragOverIndex.value = -1
  }

  const handleDrop = (event: DragEvent, targetIndex: number) => {
    event.preventDefault()
    if (draggedCalculation.value) {
      const draggedIndex = activeCalculations.findIndex(
        (c) => c.id === draggedCalculation.value!.id
      )
      if (draggedIndex !== targetIndex) {
        const [movedCalculation] = activeCalculations.splice(draggedIndex, 1)
        activeCalculations.splice(targetIndex, 0, movedCalculation)
        highlightMovedCalculation(draggedCalculation.value.id as string)
        message.success(
          `Moved "${draggedCalculation.value.display_name}" to position ${targetIndex + 1}`
        )
      }
    }
    draggedCalculation.value = null
    document.querySelectorAll('.drop-zone-active').forEach((el) => {
      el.classList.remove('drop-zone-active')
    })
  }

  const handleDragEnd = () => {
    draggedCalculation.value = null
    dragOverIndex.value = -1
  }

  const handleJsonEditorConfigurationChange = () => {
    if (selectedCalculation.value != null && activeCalculationEditConfiguration.value != null) {
      const editConfigurationJson = JSON.stringify([activeCalculationEditConfiguration.value])
      const result = calculationsJsonParsingService.tryParseEntitiesFromJson(
        editConfigurationJson,
        availableCalculationSchemas.value
      )
      if (!result.valid) {
        message.error(
          `Error parsing JSON: ${result.errors?.join(', ')}`,
          defaultMessageDurationSeconds
        )
      } else {
        const existingCalculationIndex = activeCalculations.findIndex(
          (x) => x.id === selectedCalculation.value?.id
        )
        if (
          !Array.isArray(result.parsedEntities) ||
          !result.parsedEntities?.length ||
          existingCalculationIndex <= -1
        )
          throw new Error('Error updating calculations from Json Calculation Editor value!')
        const updatedCalculation = (result.parsedEntities as Array<Calculation>)[0]
        const existingCalculation = activeCalculations[existingCalculationIndex]
        Object.assign(existingCalculation, { ...updatedCalculation, id: existingCalculation.id })
        selectedCalculation.value = existingCalculation
        // Update sort columns from JSON
        sortColumns.value = result.sort ?? []
      }
    }
  }

  const onGetFileDataResponseUpdated = (value: GetFileDataResponse) => {
    lastGetFileDataResponse.value = value
  }

  const shouldClearCustomCalculationExecution = computed(
    () => isExecuted.value && !isExecuting.value
  )

  // Ensure validation logic for wizard nav updated based on realtime shared model
  const updateSharedModelOnCalculationsChange = () => {
    const customCalcualtionExecution = shouldClearCustomCalculationExecution.value
      ? undefined
      : execution.value

    if (!isExecuting.value) {
      emit('update:pkResultsCustomCalcsFile', undefined)
      emit('update:customCalculationsExecution', customCalcualtionExecution)
    }
    const flattenedCalculations = CalculationsService.getFlattenedCalculations(activeCalculations)
    emit('update:calculations', flattenedCalculations)
  }

  const handleConfigurationUpdate = async (
    calculation: Calculation,
    config: Record<string, any>,
    calculationTypeChange?: boolean
  ) => {
    const calculationSearchResults = CalculationsSearchService.findCalculationByIdRecursive(
      activeCalculations,
      calculation.id as string
    )
    if (calculationSearchResults != null) {
      const topLevelParentCalculationIndex = activeCalculations.findIndex(
        (c) => c.id === calculationSearchResults.topLevelParent?.id
      )
      if (topLevelParentCalculationIndex > -1) {
        if (calculationTypeChange === true) {
          const newCalculationSchema = CalculationsService.tryGetCalculationSchemaByType(
            calculation._type,
            availableCalculationSchemas.value
          )
          if (newCalculationSchema == null)
            throw new Error(
              `Error getting CalculationSchema for calculation.type: ${calculation.type}!`
            )
          selectedCalculation.value = null
          let newCalculation =
            CalculationsService.createCalculationFromCalculationSchema(newCalculationSchema)
          newCalculation.description = calculation.configuration?.description
          newCalculation.configuration = { ...config }
          if (
            calculationSearchResults.topLevelParent != null &&
            calculationSearchResults.topLevelParent !== calculationSearchResults.calculation &&
            calculationSearchResults.path?.length > 1
          ) {
            throw new Error('Calculations containing child calculations is not supported.')
          } else {
            activeCalculations.splice(topLevelParentCalculationIndex, 1, newCalculation)
          }
          notifyConfigChangedStream$.next()
          nextTick(() => {
            selectedCalculation.value = activeCalculations[topLevelParentCalculationIndex]
          })
        } else if (
          calculationSearchResults.topLevelParent === calculationSearchResults.calculation
        ) {
          activeCalculations[topLevelParentCalculationIndex].configuration = { ...config }
          activeCalculations[topLevelParentCalculationIndex]._type = config._type
          notifyConfigChangedStream$.next()
        }

        updateSharedModelOnCalculationsChange()
      }
    }
  }

  const onJsonManagementCalculationsApplied = () => {
    selectedCalculation.value = activeCalculations?.length ? activeCalculations[0] : null
    updateSharedModelOnCalculationsChange()
  }

  const uiFieldGroups = computed(() => {
    return CalculationsUiService.getCalculationSchemaUiFieldGroups(
      selectedCalculation.value?.schema
    )
  })

  // Ensure first tab is selected on update of selected calculation
  // persist JSON tab selection if already active
  watch(
    [selectedCalculation, uiFieldGroups],
    ([calc, groups]) => {
      if (calc != null && groups != null && groups.length > 0) {
        // If on 'edit' tab, do not change
        if (activeCalculationTabKey.value === 'edit') return
        // If the current tab key is not in the group, or empty, set to first group
        if (
          !groups.some(
            (g: CalculationSchemaUiFieldGroup) => g.name === activeCalculationTabKey.value
          )
        ) {
          activeCalculationTabKey.value = groups[0].name
        }
      }
    },
    { immediate: true }
  )

  const goToPostProcessingDisabled = computed(() => {
    const shared = props.wizardModel?.sharedModel
    if (shared) {
      return !wizardNavigationService.getIsStepAvailable(
        AnalysisWizardStepType.PostProcessing,
        shared
      )?.isAvailable
    }
    return true
  })

  const goToWizardStepByType = (stepType: AnalysisWizardStepType) => {
    if (props.wizardModel != null) {
      wizardNavigationService.setActiveWizardStepByType(stepType, props.wizardModel)
    }
  }

  // For custom or unknown types disable superfluous input tab and just rely on Json Editor Tab
  const inputTabDisabled = computed(() => {
    if (
      selectedCalculation.value == null ||
      selectedCalculation.value._type === CalculationsService.CUSTOM_CALCULATION_TYPE
    )
      return true
    const activeSchemaType = CalculationsService.tryGetCalculationSchemaByType(
      selectedCalculation.value._type,
      availableCalculationSchemas.value
    )
    return (
      activeSchemaType == null ||
      activeSchemaType.type === CalculationsService.CUSTOM_CALCULATION_TYPE
    )
  })

  // Ensure we preselect the edit tab if view is disabled
  // but only if the mode is not already edit
  watch(
    inputTabDisabled,
    (newValue) => {
      if (newValue) {
        activeCalculationViewMode.value = { mode: 'edit' }
      } else if (activeCalculationViewMode.value.mode !== 'edit') {
        activeCalculationViewMode.value = { mode: 'view' }
      }
    },
    { immediate: true }
  )

  const updateWizardStepStatus = (statusType?: WizardStepStatusType, statusMessage?: string) => {
    if (props.wizardModel != null) {
      const stepStatusType =
        statusType != null
          ? statusType
          : props.wizardModel.sharedModel?.customCalculationsExecution?.id == null
            ? WizardStepStatusType.Warnings
            : WizardStepStatusType.Good

      let stepStatusMessage = statusMessage

      if (
        stepStatusMessage == null &&
        props.wizardModel.sharedModel?.customCalculationsExecution?.id == null
      ) {
        stepStatusMessage = 'Custom Calculations not executed'
      }

      wizardNavigationService.trySetWizardActiveStepStatus(
        props.wizardModel,
        stepStatusType,
        stepStatusMessage
      )
    }
  }

  onMounted(async () => {
    await loadAvailableCalculationSchemas()

    updateWizardStepStatus()

    // Watch input flat rules and ensure initialized (after schemas loaded)
    initializeFlatCalculations(props.customCalculations)

    watch(
      activeCalculations,
      () => {
        updateWizardStepStatus(WizardStepStatusType.Changes, 'Unsaved changes')
      },
      { immediate: false }
    )
  })

  onBeforeUnmount(() => {
    batchNotifyConfigChangedSubscription?.unsubscribe()
  })

  // --- Add this watcher to reset tab selection on selectedCalculation change ---
  watch(selectedCalculation, (newCalc, oldCalc) => {
    if (newCalc && newCalc !== oldCalc) {
      if (uiFieldGroups.value && uiFieldGroups.value.length > 0) {
        activeCalculationTabKey.value = uiFieldGroups.value[0].name
      } else if (!inputTabDisabled.value) {
        activeCalculationViewMode.value = { mode: 'view' }
      } else {
        activeCalculationViewMode.value = { mode: 'edit' }
      }
    }
  })

  const { is2XLargeTierOrLess } = useWindowSize()
  // derive live indicator from shared layoutMode so component follows active AppLayoutMode
  const { layoutMode } = useAppLayoutMode()
  const isFitToBrowser = computed(() => layoutMode.value === AppLayoutMode.FitToBrowser)

  defineExpose({
    activeCalculations,
    addCalculation,
    removeCalculation
  })
</script>

<template>
  <div
    :class="[
      'calculations-container',
      'h-full',
      'min-h-0',
      {
        'fit-to-browser': isFitToBrowser,
        'h-full': isFitToBrowser,
        'calculations-fill': isFitToBrowser
      }
    ]"
  >
    <Splitter class="h-full">
      <SplitterPanel :size="30" style="min-width: 350px">
        <div :class="['flex', 'flex-col', 'border-r-2', 'shadow-sm', 'h-full']">
          <!-- Analysis Selection UI -->
          <div class="border-b p-4 shadow-sm">
            <div class="mb-3" v-if="!analysisSelectionDisabled">
              <label class="mb-1 block text-sm font-medium">Source Analysis</label>
              <div class="mb-2 flex items-center gap-2">
                <!-- Select Analysis -->
                <Button
                  type="dashed"
                  class="file-select-button min-w-0 flex-1 text-left"
                  :disabled="isLoadingPkResultsFile || isLoadingAvailableCalculations"
                  block
                  @click="showAnalysisSelectionModal = true"
                  :title="
                    selectedAnalysisExecutionId ? 'Change selected Anaylsis' : 'Select an Analysis'
                  "
                >
                  <span class="file-select-label block w-full truncate">
                    {{ selectedAnalysisExecutionId ?? 'Select Analysis' }}
                  </span>
                </Button>

                <a-tooltip>
                  <template #title>
                    {{ 'Clear selection' }}
                  </template>
                  <Button
                    v-if="selectedAnalysisExecutionId && !isLoadingPkResultsFile"
                    type="default"
                    shape="circle"
                    size="large"
                    danger
                    class="flex items-center justify-center"
                    @click="clearAnalysisSelection"
                    title="Clear Selection"
                    tooltip="Clear Selection"
                  >
                    <Trash2 class="text-base" style="line-height: 1; margin: 0" />
                  </Button>
                </a-tooltip>
              </div>
            </div>
            <div class="mb-3">
              <label class="mb-1 block text-sm font-medium">Name</label>
              <a-tooltip placement="right">
                <template #title>
                  {{ 'Execution Name' }}
                </template>
                <Input
                  v-model:value="executionName"
                  size="large"
                  :disabled="
                    selectedAnalysisExecutionId == null ||
                    isLoadingPkResultsFile ||
                    isLoadingAvailableCalculations
                  "
                  placeholder="Execution Name"
                />
              </a-tooltip>
            </div>
            <div class="mb-3">
              <label class="mb-1 block text-sm font-medium">Output File Sort Column Order</label>
              <a-tooltip placement="right">
                <template #title>
                  {{ 'Sort columns for output file' }}
                </template>

                <div class="relative">
                  <Select
                    v-model:value="sortColumns"
                    mode="multiple"
                    :placeholder="`Select sort columns`"
                    :disabled="
                      selectedAnalysisExecutionId == null ||
                      isLoadingPkResultsFile ||
                      isLoadingAvailableCalculations
                    "
                    @change="updateSharedModelOnCalculationsChange"
                    size="large"
                    class="w-full"
                    :max-tag-count="0"
                    allow-clear
                    :options="sortColumnsOptions"
                  >
                    <template #maxTagPlaceholder="omitted">
                      {{ getSortColumnsTagLabel(omitted) }}
                    </template>
                    <template #option="{ label, pos }">
                      <span class="flex items-center gap-2">
                        <span
                          v-if="pos >= 0"
                          class="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-500 text-xs font-bold text-white"
                        >
                          {{ pos + 1 }}
                        </span>
                        <span>{{ label }}</span>
                      </span>
                    </template>
                  </Select>
                </div>
              </a-tooltip>
            </div>
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-lg font-semibold">Calculations</h3>
                <p class="mt-1 text-sm">
                  {{ activeCalculations.length }}
                  {{ activeCalculations.length === 1 ? 'calculation' : 'calculations' }} configured
                </p>
              </div>
              <div class="flex items-center gap-2 self-center">
                <a-tooltip>
                  <template #title>
                    {{ 'Add New Calculation' }}
                  </template>
                  <Button
                    :disabled="selectedAnalysisExecutionId == null || selectedPkResultsFile == null"
                    type="primary"
                    shape="circle"
                    size="large"
                    class="flex items-center justify-center"
                    @click="showCalculationSelection = true"
                    title="Add New Calculation"
                  >
                    <PlusOutlined class="text-base" style="line-height: 1; margin: 0" />
                  </Button>
                </a-tooltip>
                <a-tooltip>
                  <template #title>
                    {{ 'View/Export JSON' }}
                  </template>
                  <Button
                    type="default"
                    shape="circle"
                    size="large"
                    class="flex items-center justify-center"
                    @click="showJsonManagement = true"
                    title="View/Export JSON"
                    :disabled="
                      (!selectedAnalysisExecutionId && activeCalculations.length === 0) ||
                      isProcessingCalculations
                    "
                  >
                    <CodeOutlined class="text-base" style="line-height: 1; margin: 0" />
                  </Button>
                </a-tooltip>
              </div>
            </div>
          </div>

          <div class="calculations-list-container flex-1 p-4">
            <!-- Calculations List -->
            <div class="calculations-scroll-area min-h-0 flex-1 overflow-y-auto px-1 py-2">
              <!-- Empty State -->
              <div
                v-if="activeCalculations.length === 0"
                class="flex h-full flex-col items-center justify-start py-12 text-center"
              >
                <div class="mb-6">
                  <div
                    class="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-[var(--from-blue-gradient)] to-[var(--to-blue-gradient)]"
                  >
                    <svg
                      class="h-10 w-10 text-blue-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                      />
                    </svg>
                  </div>
                </div>
                <h3 class="mb-2 text-lg font-semibold">No Calculations Yet</h3>
                <!-- Quick Tips -->
                <div
                  class="mt-8 max-w-md rounded-lg border bg-[var(--card-lighter)] p-4 dark:bg-card"
                >
                  <h4 class="mb-2 text-sm font-medium text-blue-900 dark:text-white">
                    💡 Quick Tips
                  </h4>
                  <ul class="space-y-1 text-xs text-blue-800 dark:text-white">
                    <li>• Calculations are created and configured after selecting a file</li>
                    <li>• Use the + button to add a calculation type</li>
                    <li>• Configure each calculation in the right panel</li>
                  </ul>
                </div>
              </div>
              <div v-else class="space-y-1.5 px-1 pb-5">
                <Card
                  v-for="(calculation, index) in activeCalculations"
                  :key="calculation.id"
                  :class="[
                    'group cursor-pointer bg-[var(--card)] transition-all duration-200 hover:shadow-md dark:bg-[var(--card-light)]',
                    {
                      'ring-2 ring-blue-500': selectedCalculation?.id === calculation.id,
                      'opacity-50': draggedCalculation?.id === calculation.id,
                      'border-green-300 bg-[var(--card-light)] dark:bg-[var(--card-lighter)]':
                        dragOverIndex === index,
                      'rule-moved-highlight': highlightedCalculationId === calculation.id,
                      'rule-deleting': deletingCalculationId === calculation.id
                    }
                  ]"
                  :bodyStyle="{
                    padding: '18px'
                  }"
                  draggable="true"
                  @click="selectCalculation(calculation)"
                  @dragstart="handleDragStart($event, calculation)"
                  @dragover="handleDragOver($event, index)"
                  @dragleave="handleDragLeave"
                  @drop="handleDrop($event, index)"
                  @dragend="handleDragEnd"
                >
                  <div>
                    <div class="flex items-start justify-between">
                      <!-- Drag Handle -->
                      <div class="mr-3 flex items-center">
                        <HolderOutlined class="cursor-grab text-gray-400 active:cursor-grabbing" />
                      </div>
                      <div class="min-w-0 flex-1">
                        <h4 class="truncate text-sm font-medium leading-tight">
                          {{ calculation.display_name }}
                        </h4>
                        <p class="mt-0 line-clamp-1 text-xs text-gray-600">
                          {{ calculation.configuration?.description ?? '&nbsp;' }}
                        </p>
                      </div>
                      <!-- Calculation Controls -->
                      <div
                        class="ml-1.5 flex flex-row gap-1 opacity-0 transition-opacity group-hover:opacity-100"
                      >
                        <Button
                          type="text"
                          size="small"
                          :disabled="
                            activeCalculations.findIndex((c) => c.id === calculation.id) === 0
                          "
                          class="flex h-5 w-5 items-center justify-center"
                          @click.stop="moveCalculationUp(calculation)"
                        >
                          <ArrowUpOutlined class="text-xs" />
                        </Button>
                        <Button
                          type="text"
                          size="small"
                          :disabled="
                            activeCalculations.findIndex((c) => c.id === calculation.id) ===
                            activeCalculations.length - 1
                          "
                          class="flex h-5 w-5 items-center justify-center"
                          @click.stop="moveCalculationDown(calculation)"
                        >
                          <ArrowDownOutlined class="text-xs" />
                        </Button>
                        <Button
                          type="link"
                          size="small"
                          danger
                          class="flex h-8 w-8 items-center justify-center"
                          @click.stop="removeCalculation(calculation)"
                        >
                          <Trash2 class="text-base" style="line-height: 1; margin: 0" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </SplitterPanel>
      <SplitterPanel style="flex-basis: calc(100% - 4px)" class="bg-white dark:bg-[var(--card)]">
        <div :class="['flex', 'flex-col', { 'h-full': isFitToBrowser }]">
          <div
            class="flex items-center border-b bg-[var(--card-lighter)] p-4 dark:bg-[var(--card)]"
          >
            <div class="mr-5 min-w-0 flex-1">
              <h3 class="text-nowrap text-xs font-semibold sm:text-sm md:text-base lg:text-base">
                {{
                  selectedCalculation
                    ? `Configure: ${selectedCalculation.display_name}`
                    : 'Calculations Configuration'
                }}
              </h3>
              <p class="mt-100 text-xs sm:text-sm md:text-sm lg:text-sm">
                {{
                  selectedAnalysisExecutionId != null
                    ? selectedCalculation != null
                      ? 'Configure the selected calculation parameters below'
                      : 'Select a calculation from the left panel to configure its settings'
                    : 'Select an analysis to begin configuring calculations'
                }}
              </p>
            </div>
            <div class="flex items-center gap-1">
              <div class="mr-3">
                <ExecutionStatusComponent
                  :execution="execution"
                  :show-run-time="true"
                  ref="executionStatusComponent"
                  @notify-execution-status-changed="onExecutionStatusChanged"
                  @notify-execution-complete="onExecutionCompleted"
                />
              </div>

              <!-- Download Results -->
              <a-tooltip>
                <template #title>
                  {{
                    !execution?.id || !activeCalculations?.length
                      ? 'Calculate Parameters to download results.'
                      : 'Download Results'
                  }}
                </template>
                <Button
                  v-if="is2XLargeTierOrLess"
                  type="primary"
                  shape="circle"
                  size="large"
                  class="flex items-center justify-center"
                  :disabled="downloadResultsButtonDisabled"
                  @click="downloadResults"
                >
                  <span class="ant-button-flex-content">
                    <Loader2 v-if="isDownloadingResults" class="size-4 animate-spin" />
                    <Download
                      v-else
                      class="size-5"
                      stroke-width="1.5"
                      :class="{
                        color: !downloadResultsButtonDisabled ? 'bg-[var(--background)]' : undefined
                      }"
                    />
                  </span>
                </Button>

                <Button
                  v-else
                  type="primary"
                  :disabled="downloadResultsButtonDisabled"
                  @click="downloadResults"
                >
                  <span class="ant-button-flex-content">
                    <Loader2 v-if="isDownloadingResults" class="mr-2 size-4 animate-spin" />
                    <Download
                      v-else
                      class="size-5"
                      stroke-width="1.5"
                      :class="{
                        color: !downloadResultsButtonDisabled ? 'bg-[var(--background)]' : undefined
                      }"
                    />
                    {{ 'Download Results' }}
                  </span>
                </Button>
              </a-tooltip>

              <a-tooltip
                :open="
                  selectedAnalysisExecutionId == null ||
                  !activeCalculations?.length ||
                  isProcessingCalculations
                    ? false
                    : undefined
                "
              >
                <template #title>
                  {{
                    selectedAnalysisExecutionId == null || !activeCalculations?.length
                      ? 'Select a file and add calculations to Calculate Parameters'
                      : !isProcessingCalculations
                        ? 'Calculate Parameters'
                        : 'Calculating Parameters'
                  }}
                </template>
                <Button
                  v-if="is2XLargeTierOrLess"
                  type="primary"
                  shape="circle"
                  size="large"
                  class="flex items-center justify-center"
                  :disabled="
                    selectedAnalysisExecutionId == null ||
                    !activeCalculations?.length ||
                    isProcessingCalculations
                  "
                  @click="onSubmitCalculationsForGenerationClicked"
                >
                  <Loader2 v-if="isProcessingCalculations" class="size-4 animate-spin" />
                  <Calculator v-else class="h-4 w-4" />
                </Button>
                <Button
                  v-else
                  type="primary"
                  :disabled="
                    selectedAnalysisExecutionId == null ||
                    !activeCalculations?.length ||
                    isProcessingCalculations
                  "
                  @click="onSubmitCalculationsForGenerationClicked"
                >
                  <span class="ant-button-flex-content">
                    <Loader2 v-if="isProcessingCalculations" class="size-4 animate-spin" />
                    <Calculator v-else class="h-4 w-4" />
                    {{ 'Calculate Parameters' }}
                  </span>
                </Button>
              </a-tooltip>

              <!-- View/Export JSON-->
              <a-tooltip placement="left">
                <template #title>
                  {{ 'View/Export JSON' }}
                </template>
                <Button
                  v-if="is2XLargeTierOrLess"
                  type="primary"
                  shape="circle"
                  size="large"
                  class="flex items-center justify-center"
                  @click="showJsonManagement = true"
                  title="View/Export JSON"
                  :disabled="
                    (!selectedPkResultsFile && activeCalculations.length === 0) ||
                    isProcessingCalculations
                  "
                >
                  <CodeOutlined class="h-4 w-4" />
                </Button>
                <Button
                  v-else
                  :type="
                    selectedPkResultsFile && activeCalculations.length !== 0 ? 'button' : 'default'
                  "
                  :class="{
                    'small-button': selectedPkResultsFile && activeCalculations.length !== 0
                  }"
                  @click="showJsonManagement = true"
                  title="View/Export JSON"
                  :disabled="
                    (!selectedPkResultsFile && activeCalculations.length === 0) ||
                    isProcessingCalculations
                  "
                >
                  <span class="ant-button-flex-content">
                    <CodeOutlined class="h-4 w-4" />
                    {{ 'View/Export JSON' }}
                  </span>
                </Button>
              </a-tooltip>

              <!-- Go to Post Processing -->
              <a-tooltip placement="left" v-if="props.wizardModel != null">
                <template #title>Go to Post Processing</template>
                <Button
                  v-if="is2XLargeTierOrLess"
                  type="primary"
                  shape="circle"
                  size="large"
                  class="flex items-center justify-center"
                  :disabled="goToPostProcessingDisabled"
                  @click="
                    () => {
                      goToWizardStepByType(AnalysisWizardStepType.PostProcessing)
                    }
                  "
                >
                  <FileCog class="h-4 w-4" />
                </Button>
                <Button
                  v-else
                  type="primary"
                  :disabled="goToPostProcessingDisabled"
                  @click="
                    () => {
                      goToWizardStepByType(AnalysisWizardStepType.PostProcessing)
                    }
                  "
                >
                  <span class="ant-button-flex-content">
                    {{ 'Post Processing' }}
                    <ChevronRight class="h-4 w-4" />
                  </span>
                </Button>
              </a-tooltip>
            </div>
          </div>
          <div
            v-if="(isLoadingAvailableCalculations || isLoadingPkResultsFile) && !wizardModel"
            class="flex h-full items-center justify-center"
          >
            <div :class="['py-8', 'text-center', { 'mt-[150px]': !isFitToBrowser }]">
              <Spin size="large" />
              <p class="mt-2 text-gray-500">Loading...</p>
            </div>
          </div>
          <div v-else class="flex min-h-0 flex-1 flex-col p-4">
            <p
              v-if="selectedAnalysisExecutionId == null && !hasErrorLoadingCalculations"
              class="mt-8 text-center text-sm text-gray-600"
            >
              Select an analysis to begin configuring calculations
            </p>
            <div
              v-else-if="hasErrorLoadingCalculations"
              class="mt-8 flex justify-center text-red-400"
            >
              <span class="anticon anticon-warning-filled mr-2 text-2xl" />
              An error was encountered loading calculations.
            </div>

            <div
              v-else-if="
                selectedCalculation != null && uiFieldGroups != null && uiFieldGroups?.length
              "
              class="min-h-0 flex-1"
            >
              <a-tabs
                :key="selectedCalculation?.id"
                v-model:activeKey="activeCalculationTabKey"
                @change="onCalculationViewModeTabChange"
                :destroyInactiveTabPane="true"
              >
                <a-tab-pane
                  v-for="(fieldGroup, index) in uiFieldGroups"
                  :key="fieldGroup.name"
                  :tab="fieldGroup.name"
                >
                  <CalculationConfiguration
                    :configuration-level="1"
                    :calculation="selectedCalculation"
                    :ui-field-group="fieldGroup"
                    :available-columns="availableColumns"
                    :available-params="availableParams"
                    @configuration-updated="handleConfigurationUpdate"
                    :calculation-schemas="availableCalculationSchemas"
                    :show-description="index === 0"
                    :is-condensed-layout="true"
                  />
                </a-tab-pane>
                <a-tab-pane
                  key="edit"
                  tab="JSON"
                  :disabled="
                    CalculationsTypeGuardService.isPayloadTemplateCalculationSchema(
                      selectedCalculation.schema
                    )
                  "
                >
                  <div class="mt-[20px]">
                    <JsonCalculationConfiguration
                      v-if="activeCalculationEditConfiguration != null"
                      :key="selectedCalculation?.id"
                      :calculation-schemas="availableCalculationSchemas"
                      :available-columns="availableColumns"
                      v-model="activeCalculationEditConfiguration"
                      :editor-schema="
                        CalculationsJsonCalculationConfigurationService.getJsonEditorSchemaForCalculation()
                      "
                      :place-holder="'Configure JSON'"
                      :configuration-level="1"
                      @update:modelValue="handleJsonEditorConfigurationChange"
                      :allow-type-change="false"
                    />
                  </div>
                </a-tab-pane>

                <a-tab-pane key="pkResults" tab="PK Results File" :disabled="!pkResultsFile">
                  <div class="mt-[20px] h-full min-h-[400px]">
                    <DataViewer
                      :fileId="pkResultsFile?.id || ''"
                      :get-file-data-response="lastGetFileDataResponse"
                      @update:get-file-data-response="onGetFileDataResponseUpdated"
                    />
                  </div>
                </a-tab-pane>
              </a-tabs>
            </div>

            <a-tabs
              v-else-if="selectedCalculation != null"
              @change="onCalculationViewModeTabChange"
              :key="selectedCalculation?.id"
              v-model:activeKey="activeCalculationViewMode.mode"
              :destroyInactiveTabPane="true"
            >
              <a-tab-pane key="view" tab="Input" :disabled="inputTabDisabled">
                <CalculationConfiguration
                  :configuration-level="1"
                  :calculation="selectedCalculation"
                  :available-columns="availableColumns"
                  :available-params="availableParams"
                  @configuration-updated="handleConfigurationUpdate"
                  :calculation-schemas="availableCalculationSchemas"
                  :is-condensed-layout="true"
                />
              </a-tab-pane>
              <a-tab-pane
                key="edit"
                tab="JSON"
                :disabled="
                  CalculationsTypeGuardService.isPayloadTemplateCalculationSchema(
                    selectedCalculation.schema
                  )
                "
              >
                <div class="mt-[20px]">
                  <JsonCalculationConfiguration
                    v-if="activeCalculationEditConfiguration != null"
                    :key="selectedCalculation?.id"
                    :calculation-schemas="availableCalculationSchemas"
                    :available-columns="availableColumns"
                    v-model="activeCalculationEditConfiguration"
                    :editor-schema="
                      CalculationsJsonCalculationConfigurationService.getJsonEditorSchemaForCalculation()
                    "
                    :place-holder="'Configure JSON'"
                    :configuration-level="1"
                    @update:modelValue="handleJsonEditorConfigurationChange"
                    :allow-type-change="false"
                  />
                </div>
              </a-tab-pane>

              <a-tab-pane key="pkResults" tab="PK Results" :disabled="!pkResultsFile">
                <div class="mt-[20px]">
                  <DataViewer :fileId="pkResultsFile?.id || ''" />
                </div>
              </a-tab-pane>
            </a-tabs>
          </div>
        </div>
      </SplitterPanel>
    </Splitter>

    <!-- Calculation Selection Modal -->
    <CalculationSelectionModal
      v-model:visible="showCalculationSelection"
      :calculation-schemas="availableCalculationSchemas"
      @calculation-selected="addCalculation"
    />

    <!-- Analysis Selection Modal -->
    <Modal
      v-model:open="showAnalysisSelectionModal"
      title="Select a File"
      width="80%"
      :bodyStyle="{ height: '60vh' }"
      :footer="null"
      centered
      destroyOnClose
    >
      <div style="overflow-y: auto; height: 60vh">
        <ExecutionSelectionTable
          :execution-type-filter="[ExecutionPhaseType.NCAAnalysis]"
          @execution-selected="handleAnalysisExecutionSelected"
        />
      </div>
    </Modal>

    <!-- JSON Export Drawer -->
    <Drawer
      v-model:open="showJsonManagement"
      :destroy-on-close="true"
      title="Calculations JSON Viewer"
      placement="right"
      :width="800"
      :closable="true"
      :mask-closable="true"
    >
      <CalculationJsonManagement
        v-if="
          selectedAnalysisExecutionId != null &&
          selectedPkResultsFile != null &&
          availableCalculationSchemas != null &&
          selectedAnalysisExecutionId
        "
        :key="selectedCalculation?.id"
        :parent-execution-id="selectedAnalysisExecutionId"
        v-model:execution-name="executionName"
        :calculation-schemas="availableCalculationSchemas"
        :sortColumns="sortColumns"
        :calculations="activeCalculations"
        :visibility-changed="showJsonManagement"
        :calculations-json-parsing-service="calculationsJsonParsingService"
        @calculations-applied="onJsonManagementCalculationsApplied"
      />
    </Drawer>
  </div>
</template>

<style scoped>
  /* make the button a flexible item and allow it to shrink in a flex row */
  .file-select-button {
    text-align: center;
    flex: 1 1 0; /* take remaining space but allow shrinking */
    min-width: 0; /* CRITICAL: allow shrinking so ellipsis can take effect */
  }

  /* Ensure the Ant button inner wrapper behaves as a constrained block */
  .file-select-button .ant-btn-inner,
  .file-select-button .file-select-label {
    display: block; /* give the element a width */
    width: 100%; /* constrain to button width */
    overflow: hidden; /* required for ellipsis */
    white-space: nowrap; /* required for ellipsis */
    text-overflow: ellipsis; /* show "..." when truncated */
  }

  .calculations-container {
    display: flex;
    flex-direction: column;
    min-height: 0;
    /* Allow component to fill available space via flex but don't force parent to grow */
  }

  /* apply flex fill only when layout mode requires filling the browser viewport */
  .calculations-fill {
    flex: 1 1 0;
  }

  /* left list container becomes a flex column and will handle scrolling itself */
  .calculations-list-container {
    display: flex;
    flex-direction: column;
    /* default (FitToContent): let content size naturally and allow vertical growth */
    flex: 1 1 auto;
    min-height: 0;
    /* show vertical scrollbar when content overflows */
    overflow-y: auto;
  }
  .fit-to-browser .calculations-list-container {
    height: 100%;
    max-height: 100%;
    overflow-y: auto;
  }
  .calculations-scroll-area {
    min-height: 0;
    /* let outer container handle scrolling to avoid nested scrollbars */
    overflow: visible;
  }

  .selected-calculation {
    border: 2px solid #1890ff;
  }
  .drop-zone-active {
    height: 4px;
    background-color: rgba(37, 99, 235, 0.2);
    transition: height 0.2s ease;
  }
  .calculation-moved-highlight {
    animation: calculationMovedPulse 1.5s ease-in-out;
    border: 2px solid #10b981 !important;
    background-color: #ecfdf5 !important;
    box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.1) !important;
  }
  @keyframes calculationMovedPulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4);
    }
    50% {
      transform: scale(1.02);
      box-shadow: 0 0 0 8px rgba(16, 185, 129, 0.2);
    }
    100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
    }
  }
  .calculation-deleting {
    animation: calculationDeletePulse 0.6s ease-in-out;
    border: 2px solid #ef4444 !important;
    background-color: #fef2f2 !important;
    box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1) !important;
  }
  @keyframes calculationDeletePulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
    }
    25% {
      transform: scale(1.02);
      box-shadow: 0 0 0 6px rgba(239, 68, 68, 0.3);
    }
    75% {
      transform: scale(0.98);
      box-shadow: 0 0 0 8px rgba(239, 68, 68, 0.2);
      opacity: 0.7;
    }
    100% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
      opacity: 0;
    }
  }
  .radio-icon-center {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-size: 18px;
  }

  /* Ensure splitter fills available vertical space and panels allow inner scrolling */
  :deep(.p-splitter) {
    border: none;
    height: auto;
  }
  .fit-to-browser :deep(.p-splitter) {
    height: 100%;
  }
  :deep(.p-splitter-panel) {
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  /* Only apply full-height ant-tabs behavior when FitToBrowser is active */
  .fit-to-browser :deep(.ant-tabs) {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }
  .fit-to-browser :deep(.ant-tabs .ant-tabs-nav) {
    flex: 0 0 auto;
  }
  .fit-to-browser :deep(.ant-tabs .ant-tabs-content-holder),
  .fit-to-browser :deep(.ant-tabs .ant-tabs-content) {
    flex: 1 1 auto;
    min-height: 0 !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch;
  }
  .fit-to-browser :deep(.ant-tabs .ant-tabs-tabpane) {
    min-height: 0;
    height: 100%;
    overflow-y: auto;
  }
</style>
